import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';
import { AuthService } from './auth.service';
import type { DiagnosticScope } from '../../utils/navigation-diagnostic-scope.util';
import { UserAuth } from '../../../types/commons';

/**
 * The session check for in-page moves the router never sees: switching sheets
 * inside the filing page, and the filing page's "Leaving this page?" prompt
 * (P.O., 2026-10-08).
 *
 * The route-level counterpart is `isAuthenticatedGuard` on `/mbrs`'s
 * `canActivateChild`. Sheet switches cannot use it: the filing container sets
 * `currentSheet` (which mounts the new sheet) before it touches the URL.
 *
 * Callers await this FIRST, before any unsaved-changes dialog or prompt, so a
 * session that ended goes straight to login with today's single AUTH toast and
 * no dialog. Unsaved edits on an ended session are lost exactly as on every
 * forced logout; the backend has already ended the session, so there is
 * nothing they could still be saved to.
 *
 * Never call this on a timer — see `AuthService.verifySession()`.
 */
@Injectable({ providedIn: 'root' })
export class SessionGateService {
  private readonly authService = inject(AuthService);

  /**
   * `true` — carry on (the session is live, or the check could not get an
   * answer: a failed probe is not proof of logout).
   * `false` — stop: the session ended and `forceLogout('session-expired')`
   * has started, or a logout was already under way (signal already `false`,
   * e.g. the SignalR `ForceLogout` push ran first, or ran while this check
   * was in flight — detected through `AuthService.authTeardownGeneration`).
   *
   * `diagnosticScope` names the page or sheet being LEFT (the destination never
   * loads), so the AUTH code the login page shows points at where the user was
   * (`MBRS-S7-AUTH-12345abc`). Omitted → today's `MBRS-AUTH`. It is
   * used on the ended-session path only.
   */
  async confirmSessionBeforeLeaving(diagnosticScope?: DiagnosticScope): Promise<boolean> {
    if (this.authService.isAuthenticated() === false)
      return false;

    // Compared after the await: a logout that ran mid-flight already owns the
    // navigation, so a late answer must neither fail open nor log out again.
    const generationBefore = this.authService.authTeardownGeneration;

    let session: UserAuth;
    try {
      session = await firstValueFrom(this.authService.verifySession());
    }
    catch (error) {
      if (this.authService.authTeardownGeneration !== generationBefore)
        return false;
      if (!environment.production)
        console.warn('[SessionGate] Session check failed; carrying on.', error);
      return true;
    }

    if (this.authService.authTeardownGeneration !== generationBefore)
      return false;

    if (session.isAuthenticated)
      return true;

    this.authService.forceLogout('session-expired', { diagnosticScope });
    return false;
  }
}
